import '@xyflow/react/dist/style.css';

import { Box } from '@mui/material';

import { FlowChartProvider } from '@/components/diagrams/FlowChart/context/FlowChartProvider';
import { type UndoRedoButtonGroupProps } from '@/components/form/baseInputs/Button/UndoRedoButtonGroup';

import { DiagramEditingProvider } from '../../contexts/DiagramEditing.provider';
import { type DiagramBuilder_DiagramFragment } from '../../graphql/Diagram.generated';
import { DiagramEditorToolRail } from './DiagramEditorToolRail';
import { DiagramFlowChart } from './DiagramFlowChart';
import { DiagramLayoutStatus } from './DiagramLayoutStatus';
import { DiagramSelectionBar } from './DiagramSelectionBar';
import { DiagramViewControls } from './DiagramViewControls';

interface DiagramEditorProps {
  diagram: DiagramBuilder_DiagramFragment;
  onChange: (diagram: DiagramBuilder_DiagramFragment) => void;
  /**
   * True while the backend generates or saves the diagram. A locked canvas
   * accepts no edits and shows no edit controls.
   */
  locked?: boolean;
  history?: UndoRedoButtonGroupProps;
}

/** Shows the diagram on a canvas where the user can edit it. */
export function DiagramEditor({
  diagram,
  onChange,
  locked = false,
  history,
}: DiagramEditorProps) {
  return (
    <FlowChartProvider editable={!locked} allowGrouping={false}>
      <DiagramEditingProvider
        diagram={diagram}
        onChange={onChange}
        locked={locked}
      >
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            flex: 1,
            display: 'flex',
            backgroundColor: 'background.paper',
            color: 'text.primary',
          }}
        >
          <DiagramEditorToolRail />
          <Box
            sx={{
              minWidth: 0,
              minHeight: 0,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DiagramLayoutStatus />
            <DiagramFlowChart>
              {!locked && <DiagramSelectionBar />}
              <DiagramViewControls history={history} />
            </DiagramFlowChart>
          </Box>
        </Box>
      </DiagramEditingProvider>
    </FlowChartProvider>
  );
}
